import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getRajasthanDailyPeak,
  getRajasthanMonthlyPeakDiscom,
  getDailyDiscomDrawalSchedule,
  getMonthlyDiscomDrawalSchedule,
  getDailyTotalDrawalSchedule,
  getMonthlyTotalDrawalSchedule,
} from "../../../Rest_api/DemoRestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import { useDispatch, useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../../Rajasthan/const";
import { setLoadingFilterName } from "../../../store/state/FilterLoadingState";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

const X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const GROUP_BY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];
const DISCOM = [
  { label: "Discom 1", key: "Discom 1" },
  { label: "Discom 2", key: "Discom 2" },
  { label: "Discom 3", key: "Discom 3" },
];
const TYPE = ["Schedule", "Drawal"];
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];
const GROUP_BY_ARRAY = ["Discom Load", "Total Discom Load", "RVPN Drawal"];

// Helper function to get month with year
function getMonthWithYear(month: number, year: number): string {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const yearShort = year.toString().slice(-2);
  return `${monthNames[month - 1]}-${yearShort}`;
}

// Helper function to parse month-year string back to comparable values
function parseMonthYear(monthYearStr: string | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) return { month: 0, year: 0 };
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parts = monthYearStr.split("-");
  if (parts.length !== 2) return { month: 0, year: 0 };
  const [monthStr, yearStr] = parts;
  const month = monthNames.indexOf(monthStr) + 1;
  const year = 2000 + parseInt(yearStr);
  return { month, year };
}

// Helper to parse "MM-YYYY" format from API to "Mon-YY" format
function parseApiMonthYear(apiMonthYear: string | undefined): string {
  if (!apiMonthYear) return "";
  const parts = apiMonthYear.split("-");
  if (parts.length !== 2) return "";
  const [month, year] = parts;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthIndex = parseInt(month) - 1;
  if (monthIndex < 0 || monthIndex > 11) return "";
  const yearShort = year.slice(-2);
  return `${monthNames[monthIndex]}-${yearShort}`;
}

interface RajasthanPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  metrics: DiscomLoadMetrics | null;
}

export function RajasthanPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: RajasthanPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  // ▶ UPDATED — three separate data states (from original)
  const [rvpnPeakData, setRvpnPeakData] = useState<any[]>([]);
  const [totalPeakData, setTotalPeakData] = useState<any[]>([]);
  const [discomPeakData, setDiscomPeakData] = useState<any[]>([]);

  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const dispatch = useDispatch();

  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);

  const createChartObject = useCallback(
    (
      key: string,
      index: number,
      suffix: string,
      displayName: string,
      stackSuffix: string,
    ) => {
      return {
        dataKey: `${key}_${suffix}`,
        name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? stackSuffix : undefined,
      };
    },
    [selectedChartType],
  );

  // ▶ UPDATED — Promise.all fetching all 3 datasets; no selectedGroupKeys dependency
  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates || !dates.startDate || !dates.endDate) {
        setRvpnPeakData([]);
        setTotalPeakData([]);
        setDiscomPeakData([]);
        setLoading(false);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const [rvpnRaw, totalRaw, discomRaw] = await Promise.all([
        selectedXaxis.key === "month"
          ? getRajasthanMonthlyPeakDiscom(startDateStr, endDateStr)
          : getRajasthanDailyPeak(startDateStr, endDateStr),
        selectedXaxis.key === "month"
          ? getMonthlyTotalDrawalSchedule(startDateStr, endDateStr)
          : getDailyTotalDrawalSchedule(startDateStr, endDateStr),
        selectedXaxis.key === "month"
          ? getMonthlyDiscomDrawalSchedule(startDateStr, endDateStr)
          : getDailyDiscomDrawalSchedule(startDateStr, endDateStr),
      ]);

      // 1. Process RVPN
      let processedRvpn = Array.isArray(rvpnRaw) ? rvpnRaw : [];
      if (selectedXaxis.key === "month") {
        processedRvpn = processedRvpn.map((item) => ({
          ...item,
          month: getMonthWithYear(item.month, item.year),
        }));
      }

      // 2. Process Total Discom
      let processedTotal: any[] = [];
      if (selectedXaxis.key === "month") {
        if (Array.isArray(totalRaw)) {
          processedTotal = totalRaw.map((item: any) => ({
            ...item,
            month: getMonthWithYear(item.month, item.year),
            peak_schedule_mw: item.peak_schedule_mw || 0,
            peak_drawal_mw: item.peak_drawal_mw || 0,
            morning_peak_schedule_mw: item.morning_peak_schedule_mw || 0,
            morning_peak_drawal_mw: item.morning_peak_drawal_mw || 0,
            evening_peak_schedule_mw: item.evening_peak_schedule_mw || 0,
            evening_peak_drawal_mw: item.evening_peak_drawal_mw || 0,
            average_schedule_mw: item.average_schedule_mw || 0,
            average_drawal_mw: item.average_drawal_mw || 0,
          }));
        }
      } else {
        const dailyArr = Array.isArray(totalRaw)
          ? totalRaw
          : ((totalRaw as any)?.data ?? []);
        processedTotal = dailyArr.map((item: any) => {
          const morningData = item.formatted_data?.find(
            (fd: any) => fd.time_period === "Morning",
          );
          const eveningData = item.formatted_data?.find(
            (fd: any) => fd.time_period === "Evening",
          );
          return {
            ...item,
            peak_schedule_mw: item.daily_peak_schedule_mw || 0,
            peak_drawal_mw: item.daily_peak_drawal_mw || 0,
            morning_peak_schedule_mw: morningData?.max_schedule || 0,
            morning_peak_drawal_mw: morningData?.max_drawal || 0,
            evening_peak_schedule_mw: eveningData?.max_schedule || 0,
            evening_peak_drawal_mw: eveningData?.max_drawal || 0,
            average_schedule_mw: item.avg_daily_schedule_mw || 0,
            average_drawal_mw: item.avg_daily_drawal_mw || 0,
          };
        });
      }

      // 3. Process Discom Load
      let processedDiscom: any[] = [];
      if (selectedXaxis.key === "month") {
        if (Array.isArray(discomRaw)) {
          processedDiscom = discomRaw.map((item) => ({
            ...item,
            month: getMonthWithYear(item.month, item.year),
            peak_schedule_mw: item.peak_schedule_mw || 0,
            peak_drawal_mw: item.peak_drawal_mw || 0,
            morning_peak_schedule_mw: item.morning_peak_schedule_mw || 0,
            morning_peak_drawal_mw: item.morning_peak_drawal_mw || 0,
            evening_peak_schedule_mw: item.evening_peak_schedule_mw || 0,
            evening_peak_drawal_mw: item.evening_peak_drawal_mw || 0,
            average_schedule_mw: item.average_schedule_mw || 0,
            average_drawal_mw: item.average_drawal_mw || 0,
          }));
        }
      } else {
        processedDiscom = Array.isArray(discomRaw) ? discomRaw : [];
      }

      setRvpnPeakData(processedRvpn);
      setTotalPeakData(processedTotal);
      setDiscomPeakData(processedDiscom);

      // Unique dates from all datasets combined
      const dateSet = new Set<string>();
      [...processedRvpn, ...processedTotal, ...processedDiscom].forEach(
        (item) => {
          if (item.date) dateSet.add(item.date);
        },
      );
      setUniqueDate(
        Array.from(dateSet).sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime(),
        ),
      );
    } catch (error) {
      console.error("Error in data fetching:", error);
      setRvpnPeakData([]);
      setTotalPeakData([]);
      setDiscomPeakData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  // ▶ UPDATED — reads from three separate states; dependency array updated
  useEffect(() => {
    let allData: any[] = [];
    if (selectedGroupKeys === "RVPN Drawal") {
      allData = rvpnPeakData;
    } else if (selectedGroupKeys === "Total Discom Load") {
      allData = totalPeakData;
    } else {
      allData = discomPeakData;
    }

    if (allData.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // Deduplicate if not "Discom Load"
    let displayData = allData;
    if (selectedGroupKeys !== "Discom Load") {
      const dataMap = new Map<string, any>();
      allData.forEach((item: any) => {
        const discomPart = item.discom ? `_${item.discom}` : "";
        const uniqueKey =
          selectedXaxis.key === "month"
            ? `${item.month}_${item.year}${discomPart}`
            : `${item.date}${discomPart}`;
        if (!dataMap.has(uniqueKey)) {
          dataMap.set(uniqueKey, item);
        }
      });
      displayData = Array.from(dataMap.values());
    }

    let filteredData = [...displayData];

    // ▶ UPDATED — discom filter also excludes "Total Discom Load" (from original)
    if (
      selectedGroupKeys !== "RVPN Drawal" &&
      selectedGroupKeys !== "Total Discom Load"
    ) {
      if (selectedDiscom.length > 0 && selectedDiscom.length < DISCOM.length) {
        filteredData = filteredData.filter((item) =>
          selectedDiscom.includes(item.discom),
        );
      }
    }

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }

    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "month") {
        if (!a.month || !b.month) return 0;
        const aMonthYear = parseMonthYear(a.month);
        const bMonthYear = parseMonthYear(b.month);
        if (aMonthYear.year !== bMonthYear.year)
          return aMonthYear.year - bMonthYear.year;
        return aMonthYear.month - bMonthYear.month;
      } else {
        if (!a.date || !b.date) return 0;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
    });

    const conversionFactor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;
    const convertedData = filteredData.map((item) => ({
      ...item,
      peak_mw: item.peak_mw ? item.peak_mw * conversionFactor : 0,
      morning_peak_mw: item.morning_peak_mw
        ? item.morning_peak_mw * conversionFactor
        : 0,
      evening_peak_mw: item.evening_peak_mw
        ? item.evening_peak_mw * conversionFactor
        : 0,
      average_mw: item.average_mw
        ? item.average_mw * conversionFactor
        : undefined,
      peak_schedule_mw: item.peak_schedule_mw
        ? item.peak_schedule_mw * conversionFactor
        : 0,
      peak_drawal_mw: item.peak_drawal_mw
        ? item.peak_drawal_mw * conversionFactor
        : 0,
      morning_peak_schedule_mw: item.morning_peak_schedule_mw
        ? item.morning_peak_schedule_mw * conversionFactor
        : 0,
      morning_peak_drawal_mw: item.morning_peak_drawal_mw
        ? item.morning_peak_drawal_mw * conversionFactor
        : 0,
      evening_peak_schedule_mw: item.evening_peak_schedule_mw
        ? item.evening_peak_schedule_mw * conversionFactor
        : 0,
      evening_peak_drawal_mw: item.evening_peak_drawal_mw
        ? item.evening_peak_drawal_mw * conversionFactor
        : 0,
      average_schedule_mw: item.average_schedule_mw
        ? item.average_schedule_mw * conversionFactor
        : 0,
      average_drawal_mw: item.average_drawal_mw
        ? item.average_drawal_mw * conversionFactor
        : 0,
    }));

    let aggregationKeys: string[] = [];
    let groupByKeys: string[] = [];

    if (selectedGroupKeys === "RVPN Drawal") {
      aggregationKeys = ["peak_mw", "morning_peak_mw", "evening_peak_mw"];
      groupByKeys = [];
    } else if (selectedGroupKeys === "Discom Load") {
      aggregationKeys = [
        "peak_schedule_mw",
        "peak_drawal_mw",
        "morning_peak_schedule_mw",
        "morning_peak_drawal_mw",
        "evening_peak_schedule_mw",
        "evening_peak_drawal_mw",
        "average_schedule_mw",
        "average_drawal_mw",
      ];
      groupByKeys = ["discom"];
    } else {
      // Total Discom Load
      aggregationKeys = [
        "peak_schedule_mw",
        "peak_drawal_mw",
        "morning_peak_schedule_mw",
        "morning_peak_drawal_mw",
        "evening_peak_schedule_mw",
        "evening_peak_drawal_mw",
        "average_schedule_mw",
        "average_drawal_mw",
      ];
      groupByKeys = [];
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: groupByKeys,
      volumeKey: ``,
      aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: ["date"],
      priceKey: "",
      data: convertedData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    // ▶ UPDATED — three data states replace single `data`
    rvpnPeakData,
    totalPeakData,
    discomPeakData,
    selectedXaxis,
    selectedUnit,
    selectedDate,
    selectedDiscom,
    selectedGroupKeys,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const legends = useMemo(() => {
    if (selectedGroupKeys === "RVPN Drawal") {
      const suffixes = [
        { suffix: "peak_mw", displayName: "Day Peak" },
        { suffix: "morning_peak_mw", displayName: "Morning Peak" },
        { suffix: "evening_peak_mw", displayName: "Evening Peak" },
      ];
      return suffixes.flatMap(({ suffix, displayName }) =>
        chartKeys.map((key, index) =>
          createChartObject(key, index, suffix, displayName, suffix),
        ),
      );
    } else {
      const peakTypes = [
        { field: "peak", displayName: "Day Peak" },
        { field: "morning_peak", displayName: "Morning Peak" },
        { field: "evening_peak", displayName: "Evening Peak" },
      ];

      const legends: any[] = [];
      chartKeys.forEach((key, keyIndex) => {
        selectedSuffix.forEach(({ suffix, displayName, stackSuffix }) => {
          peakTypes.forEach((peak) => {
            const fieldSuffix = `${peak.field}_${suffix}_mw`;
            const legendDisplayName = `${key} ${peak.displayName} ${displayName}`;
            legends.push({
              dataKey: `${key}_${fieldSuffix}`,
              name: legendDisplayName,
              legendColor: undefined,
              stroke: undefined,
              type: selectedChartType.value,
              stackId:
                selectedChartType.stackId !== undefined
                  ? stackSuffix
                  : undefined,
            });
          });
        });
      });
      return legends;
    }
  }, [
    selectedGroupKeys,
    chartKeys,
    selectedSuffix,
    selectedChartType,
    createChartObject,
  ]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") return ["date"];
    return ["month", "year"];
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const xDataKey = selectedXaxis.values[0];

  const commonSummaryProps = {
    legends: visibleLegends as any,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Discom Load Peak Analysis Demo",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => onUnitChange(item)}
          />
          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Discom"
              items={DISCOM.map((item) => item.label)}
              selectedItems={selectedDiscom}
              onSelect={(item, index) => {}}
              setSelectedItems={(item) => {
                // ▶ UPDATED — guard for Total Discom Load (from original)
                if (selectedGroupKeys === "Total Discom Load") {
                  dispatch(setLoadingFilterName(""));
                  return;
                }
                onDiscomChange(item);
              }}
            />
          )}
          <FilterMenu
            title="Group By"
            items={GROUP_BY_ARRAY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              onGroupByChange(index);
            }}
          />
          {selectedGroupKeys !== "RVPN Drawal" && (
            <FilterMenu
              title="Type"
              items={TYPE}
              selectedItems={selectedSuffix.map(
                (item) => item?.displayName || "",
              )}
              setSelectedItems={(selected: string[]) => {
                onTypeChange(selected);
              }}
            />
          )}
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">
                  Discom Load Peak Analysis Demo ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart") {
                        downloadChartTableCSV({ ...commonSummaryProps });
                      } else if (dataTab.label === "Summary") {
                        downloadSummaryCSV(commonSummaryProps);
                      } else if (dataTab.label === "Table") {
                        downloadChartTableCSV(commonSummaryProps);
                      }
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedGroupKeys}-${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  secondXDataKey={undefined}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...commonSummaryProps} />
              )}
            </>
          )}
        </div>

        {/* Demo footer — 2-col, no Source tooltip */}
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
